// Modal para subir un documento nuevo a la carpeta activa (drag & drop o
// selector de archivo), con metadata básica (nombre, versión, descripción).
import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useDocs } from "../context/DocsContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../api";
import { X, Upload, FileText, CheckCircle, Loader, FolderOpen } from "lucide-react";

export default function UploadModal({ onClose, category, company, isTemplates }) {
  const { addDoc } = useDocs();
  const { isAdmin } = useAuth();
  const fileRef = useRef();
  const [file, setFile]   = useState(null);
  const [form, setForm]   = useState({ name: "", version: "v1.0", description: "" });
  const [copyMode, setCopyMode] = useState("all");
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [done, setDone]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);
  const canChooseCopies = isTemplates && isAdmin;

  useEffect(() => {
    if (!canChooseCopies) return;
    api.getCompanies().then((result) => {
      setCompanies(Array.isArray(result) ? result : []);
    }).catch(() => {
      setCompanies([]);
    });
  }, [canChooseCopies]);

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    setForm(prev => ({ ...prev, name: prev.name || f.name.replace(/\.[^.]+$/, "") }));
  };

  const toggleCompany = (id) => {
    setSelectedCompanies((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
  };

  const fmtSize = (b) => b > 1e6 ? `${(b/1e6).toFixed(1)} MB` : `${(b/1e3).toFixed(0)} KB`;

  const handleSubmit = async () => {
    if (!form.name || !form.description.trim() || !file || !category) return;
    if (canChooseCopies && copyMode === "selected" && selectedCompanies.length === 0) {
      setError("Debes seleccionar al menos una empresa.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", form.name);
      fd.append("category", category);
      fd.append("version", form.version);
      fd.append("description", form.description);
      if (canChooseCopies) {
        fd.append("copy_mode", copyMode);
        fd.append("target_company_ids", JSON.stringify(selectedCompanies));
      }
      await addDoc(fd);
      setDone(true);
      setTimeout(onClose, 1400);
    } catch (e) {
      setError("Error subiendo: " + e.message + ". ¿Está corriendo el backend?");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div className="modal-overlay"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <motion.div className="modal"
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 12 }}
        transition={{ type: "spring", stiffness: 380, damping: 30, mass: 0.7 }}>
        <div className="modal-header">
          <h2>Subir documento</h2>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>

        {done ? (
          <div className="modal-success">
            <CheckCircle size={48} className="success-icon" />
            <p>¡Documento subido correctamente!</p>
          </div>
        ) : (
          <>
            <div style={{ flex: 1, overflowY: "auto" }}>
              <div
                className={`dropzone ${dragging ? "dragging" : ""} ${file ? "has-file" : ""}`}
                onClick={() => fileRef.current.click()}
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
              >
                <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xls,.docx,.doc,.png,.jpg" hidden
                  onChange={e => handleFile(e.target.files[0])} />
                {file ? (
                  <><FileText size={36} style={{ color: "var(--green)" }} /><p style={{ fontWeight: 600 }}>{file.name}</p><p className="drop-sub">{fmtSize(file.size)}</p></>
                ) : (
                  <><Upload size={36} className="drop-icon" /><p>Arrastra tu archivo aquí</p><p className="drop-sub">PDF, Excel, Word, Imágenes</p></>
                )}
              </div>

              {error && <p style={{ color: "#C53030", fontSize: "0.78rem", padding: "8px 24px" }}>{error}</p>}

              <div className="modal-form">
                <div className="form-row">
                  <label>Nombre del documento *</label>
                  <input className="form-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ej: Procedimiento de trabajo seguro" />
                </div>
                <div className="form-row">
                  <label>Carpeta</label>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "9px 12px", borderRadius: 8, background: "#F5F3FF",
                    border: "1px solid #E9D5FF", fontSize: "0.84rem", color: "#6B21A8", fontWeight: 500,
                  }}>
                    <FolderOpen size={15} /> {category}
                  </div>
                </div>
                {company && (
                  <div className="form-row">
                    <label>Empresa</label>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "9px 12px", borderRadius: 8, background: "#ECFDF5",
                      border: "1px solid #A7F3D0", fontSize: "0.84rem", color: "#047857", fontWeight: 500,
                    }}>
                      {company.name}
                    </div>
                  </div>
                )}
                <div className="form-row">
                  <label>Versión</label>
                  <input className="form-input" value={form.version} onChange={e => setForm(p => ({ ...p, version: e.target.value }))} placeholder="v1.0" />
                </div>
                <div className="form-row">
                  <label>Descripción *</label>
                  <textarea className="form-input form-textarea" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3} />
                </div>
                {canChooseCopies && (
                  <>
                    <div className="form-row">
                      <label>Copiar a empresas</label>
                      <select className="form-input" value={copyMode} onChange={e => setCopyMode(e.target.value)}>
                        <option value="none">Solo original</option>
                        <option value="all">Todas las empresas</option>
                        <option value="selected">Empresas seleccionadas</option>
                      </select>
                    </div>
                    {copyMode === "selected" && (
                      <div className="form-row">
                        <label>Selecciona las empresas</label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {companies.map((item) => {
                            const active = selectedCompanies.includes(item.id);
                            return (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => toggleCompany(item.id)}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "6px 12px",
                                  borderRadius: 8,
                                  border: `1px solid ${active ? "var(--primary)" : "rgba(0,0,0,0.1)"}`,
                                  background: active ? "#F0FDFA" : "rgba(0,0,0,0.03)",
                                  color: active ? "var(--primary)" : "var(--text-muted)",
                                  cursor: "pointer",
                                  fontFamily: "inherit",
                                  fontSize: "0.78rem",
                                }}
                              >
                                {item.name}
                              </button>
                            );
                          })}
                          {companies.length === 0 && (
                            <p style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                              No hay empresas disponibles para seleccionar.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={onClose}>Cancelar</button>
              <button className="btn-primary" onClick={handleSubmit} disabled={!form.name || !form.description.trim() || !file || loading}>
                {loading ? <><Loader size={15} className="spin" /> Subiendo...</> : <><Upload size={15} /> Guardar</>}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
